import { getGovernanceSummaryFromCompany } from "utils/companyGovernanceSummaryReferral";
import { Constants } from "config/constants";

export const ValidateTransactionSchema = (
  company,
  transactions,
  companyDashboardSlice
) => {
  // Safeguard against undefined companyDashboardSlice
  const { companyOfficials } = companyDashboardSlice || {};

  // Safeguard against undefined nominalShares
  const nominalShares = company.nominalShares || [];
  const summary = getGovernanceSummaryFromCompany(company);

  const sumNominalCapital = nominalShares
    .map((share) => share.sharesValue * share.sharesNumber)
    .reduce((a, b) => a + b, 0);

  // Transaction validation with director-specific checks
  const minDirectors =
    summary?.directorsLimit?.minimum ||
    (company.type === Constants.ENTITY_TYPE_AGAINST_TYPE.PVT ? 1 : 2);
  const maxDirectors =
    summary?.directorsLimit?.maximum ||
    (company.type === Constants.ENTITY_TYPE_AGAINST_TYPE.PVT ? 10 : 15);
  const directors = (companyOfficials?.items || []).filter(
    (official) =>
      official.designation === Constants.APPLICANT_CAPACITIES.DIRECTOR ||
      official.designation ===
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
  );
  let projectedDirectorCount = directors.length;

  const validationResults = transactions.map((transaction) => {
    const result = {
      transactionId: transaction.id || "unknown",
      changeType: transaction.changeType,
      applicationType: transaction.applicationType,
      isValid: true,
      message: "Transaction not subject to PVT cessation validation.",
    };

    const isDirector =
      transaction.applicationType === Constants.APPLICANT_CAPACITIES.DIRECTOR ||
      transaction.applicationType ===
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER;

    if (isDirector && transaction.changeType === "APPOINTMENT") {
      projectedDirectorCount++;
      if (projectedDirectorCount > maxDirectors) {
        result.isValid = false;
        result.message = `Director appointment exceeds maximum of ${maxDirectors} directors.`;
      } else {
      }
    } else if (isDirector && transaction.changeType === "CESSATION") {
      projectedDirectorCount--;
      const hasAppointment = transactions.some(
        (tx) =>
          tx.changeType === "APPOINTMENT" &&
          (tx.applicationType === Constants.APPLICANT_CAPACITIES.DIRECTOR ||
            tx.applicationType ===
              Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER) &&
          tx.id !== transaction.id
      );
      if (projectedDirectorCount < minDirectors && !hasAppointment) {
        result.isValid = false;
        result.message = `Director cessation results in fewer than ${minDirectors} directors without a compensating appointment.`;
      } else {
      }
    }

    return result;
  });

  // Governance validation with cessation/appointment pair resolution
  const governanceValidation = validateGovernanceRules(
    company,
    companyOfficials?.items ?? [],
    summary,
    transactions,
    validationResults,
    sumNominalCapital
  );

  return {
    governanceSummary: summary,
    validationResults,
    governanceValidation,
  };
};

// Helper function to validate governance rules
const validateGovernanceRules = (
  company,
  companyOfficials,
  summary,
  transactions,
  validationResults,
  sumNominalCapital
) => {
  // Ensure we always have arrays to work with

  companyOfficials = companyOfficials || [];

  const results = {
    isValid: true,
    messages: [],
  };

  // Filter officials by designation
  const directors = companyOfficials.filter(
    (official) =>
      official.designation === Constants.APPLICANT_CAPACITIES.DIRECTOR ||
      official.designation ===
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
  );

  const secretaries = companyOfficials.filter(
    (official) =>
      official.designation === Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY
  );

  const shareholders = companyOfficials.filter(
    (official) =>
      official.designation === Constants.APPLICANT_CAPACITIES.SHAREHOLDER ||
      official.designation ===
        Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER ||
      official.designation ===
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER
  );

  // Natural person directors (KE Citizen, Foreign Resident, or Foreigner)
  const naturalPersonDirectors = directors.filter(
    (d) =>
      d.idType === Constants.ID_TYPES.KENYAN_CITIZEN ||
      d.idType === Constants.ID_TYPES.FOREIGN_RESIDENT ||
      d.idType === Constants.ID_TYPES.FOREIGNER
  );

  // Foreign directors specifically
  const foreignerDirectors = directors.filter(
    (d) => d.idType === Constants.ID_TYPES.FOREIGNER
  );

  // Check for duplicate persons in CO Tab
  const personIds = companyOfficials
    .map((official) => official.personId || official._id)
    .filter(Boolean);

  // Calculate share allocation percentage
  const shareAllocationValidation = validateShareAllocation(
    company,
    shareholders
  );
  if (!shareAllocationValidation.isValid) {
    results.messages.push(...shareAllocationValidation.messages);
    results.isValid = false;
  }

  // Apply transaction effects to get projected counts
  const projectedCounts = calculateProjectedCounts(
    directors,
    secretaries,
    shareholders,
    naturalPersonDirectors,
    foreignerDirectors,
    validationResults,
    transactions
  );

  // Company type-specific validations
  if (company.type === Constants.ENTITY_TYPE_AGAINST_TYPE.PVT) {
    validatePVTCompany(
      results,
      projectedCounts,
      summary,
      sumNominalCapital,
      transactions
    );
  } else if (company.type === Constants.ENTITY_TYPE_AGAINST_TYPE.PLC) {
    validatePLCCompany(results, projectedCounts, summary);
  }

  return results;
};

// Helper function to calculate projected counts after transactions
const calculateProjectedCounts = (
  directors,
  secretaries,
  shareholders,
  naturalPersonDirectors,
  foreignerDirectors,
  validationResults,
  transactions
) => {
  // Create projection based on current state
  let projectedDirectors = directors.length;
  let projectedSecretaries = secretaries.length;
  let projectedShareholders = shareholders.length;
  let projectedNaturalPersonDirectors = naturalPersonDirectors.length;
  let projectedForeignerDirectors = foreignerDirectors.length;

  // Apply transaction effects
  validationResults.forEach((result) => {
    const transaction = transactions.find(
      (tx) => tx.id === result.transactionId
    );
    if (!transaction) return;

    const isDirector =
      transaction.applicationType === Constants.APPLICANT_CAPACITIES.DIRECTOR ||
      transaction.applicationType ===
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER;
    const isSecretary =
      transaction.applicationType ===
      Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY;
    const isShareholder =
      transaction.applicationType ===
        Constants.APPLICANT_CAPACITIES.SHAREHOLDER ||
      transaction.applicationType ===
        Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER ||
      transaction.applicationType ===
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER;

    if (result.changeType === "APPOINTMENT") {
      if (isDirector) {
        projectedDirectors++;
        if (
          transaction.idType === Constants.ID_TYPES.KENYAN_CITIZEN ||
          transaction.idType === Constants.ID_TYPES.FOREIGN_RESIDENT ||
          transaction.idType === Constants.ID_TYPES.FOREIGNER
        ) {
          projectedNaturalPersonDirectors++;
        }
        if (transaction.idType === Constants.ID_TYPES.FOREIGNER) {
          projectedForeignerDirectors++;
        }
      }
      if (isSecretary) projectedSecretaries++;
      if (isShareholder) projectedShareholders++;
    } else if (result.changeType === "CESSATION") {
      if (isDirector) {
        projectedDirectors--;
        const ceasingDirector = [...directors, ...naturalPersonDirectors].find(
          (d) =>
            d._id === transaction.personId ||
            d.personId === transaction.personId
        );
        if (ceasingDirector) {
          if (
            ceasingDirector.idType === Constants.ID_TYPES.KENYAN_CITIZEN ||
            ceasingDirector.idType === Constants.ID_TYPES.FOREIGN_RESIDENT ||
            ceasingDirector.idType === Constants.ID_TYPES.FOREIGNER
          ) {
            projectedNaturalPersonDirectors--;
          }
          if (ceasingDirector.idType === Constants.ID_TYPES.FOREIGNER) {
            projectedForeignerDirectors--;
          }
        }
      }
      if (isSecretary) projectedSecretaries--;
      if (isShareholder) projectedShareholders--;
    }
  });

  return {
    directors: Math.max(0, projectedDirectors),
    secretaries: Math.max(0, projectedSecretaries),
    shareholders: Math.max(0, projectedShareholders),
    naturalPersonDirectors: Math.max(0, projectedNaturalPersonDirectors),
    foreignerDirectors: Math.max(0, projectedForeignerDirectors),
  };
};

// PVT Company validation
const validatePVTCompany = (
  results,
  projectedCounts,
  summary,
  sumNominalCapital,
  transactions
) => {
  const minDirectors = summary?.directorsLimit?.minimum || 1;
  const maxDirectors = summary?.directorsLimit?.maximum || 10;

  // At least one Director
  if (projectedCounts.directors < minDirectors) {
    results.messages.push(
      `Company requires at least ${minDirectors} Director(s). Please appoint a Director.`
    );
    results.isValid = false;
  } else {
  }

  // Max Directors check
  if (projectedCounts.directors > maxDirectors) {
    results.messages.push(
      `Company exceeds maximum of ${maxDirectors} Directors. Please cease a Director.`
    );
    results.isValid = false;
  } else {
  }

  // At least one Director must be a natural person
  if (projectedCounts.naturalPersonDirectors < 1) {
    results.messages.push(
      "Company requires at least one Director to be a natural person (KE Citizen, Foreign Resident or Foreigner). Please appoint a natural person as a Director."
    );
    results.isValid = false;
  } else {
  }

  // If all Directors are Foreigners, require Company Secretary
  if (
    projectedCounts.directors > 0 &&
    projectedCounts.foreignerDirectors === projectedCounts.directors &&
    projectedCounts.secretaries < 1
  ) {
    results.messages.push(
      "Company requires a Company Secretary if all Director(s) are Foreigner(s). Please appoint a Company Secretary."
    );
    results.isValid = false;
  } else {
  }

  // If Share Capital >= KES 5 million, Company Secretary is mandatory
  if (sumNominalCapital >= 5000000 && projectedCounts.secretaries < 1) {
    const hasSecretaryAppointment = transactions.some(
      (tx) =>
        tx.changeType === "APPOINTMENT" &&
        tx.applicationType === Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY
    );
    if (!hasSecretaryAppointment) {
      results.messages.push(
        "Company requires a Company Secretary if Share Capital is KES 5 million or above. Please appoint a Company Secretary."
      );
      results.isValid = false;
    } else {
    }
  } else {
  }

  // Company Secretary limits (Min 1, Max 2 if any)
  if (projectedCounts.secretaries > 2) {
    results.messages.push(
      "Company can have a maximum of 2 Company Secretaries. Please cease a Company Secretary."
    );
    results.isValid = false;
  } else {
  }

  // Shareholder requirements
  if (projectedCounts.shareholders < 1) {
    results.messages.push(
      "Company requires at least one Shareholder. Please appoint a Shareholder."
    );
    results.isValid = false;
  } else {
  }

  if (projectedCounts.shareholders > 50) {
    const excessShareholders = projectedCounts.shareholders - 50;
    results.messages.push(
      `Company exceeds 50 Shareholders. ESOP Confirmations required for ${excessShareholders} entries.`
    );
  } else {
  }
};

// PLC Company validation
const validatePLCCompany = (results, projectedCounts, summary) => {
  const minDirectors = summary?.directorsLimit?.minimum || 2;
  const maxDirectors = summary?.directorsLimit?.maximum || 15;

  // At least two Directors
  if (projectedCounts.directors < minDirectors) {
    results.messages.push(
      `Company requires at least ${minDirectors} Directors. Please appoint ${
        minDirectors - projectedCounts.directors
      } more Director(s).`
    );
    results.isValid = false;
  } else {
  }

  // Max Directors check
  if (projectedCounts.directors > maxDirectors) {
    results.messages.push(
      `Company exceeds maximum of ${maxDirectors} Directors. Please cease ${
        projectedCounts.directors - maxDirectors
      } Director(s).`
    );
    results.isValid = false;
  } else {
  }

  // At least one Director must be a natural person
  if (projectedCounts.naturalPersonDirectors < 1) {
    results.messages.push(
      "Company requires at least one Director to be a natural person (KE Citizen, Foreign Resident or Foreigner). Please appoint a natural person as a Director."
    );
    results.isValid = false;
  } else {
  }

  // Company Secretary requirements (Min 1, Max 2)
  if (projectedCounts.secretaries < 1) {
    results.messages.push(
      "Company requires at least 1 Company Secretary. Please appoint a Company Secretary."
    );
    results.isValid = false;
  } else {
  }

  if (projectedCounts.secretaries > 2) {
    results.messages.push(
      `Company can have a maximum of 2 Company Secretaries. Please cease ${
        projectedCounts.secretaries - 2
      } Company Secretary(ies).`
    );
    results.isValid = false;
  } else {
  }

  // Shareholder requirements (Min 1, no Max)
  if (projectedCounts.shareholders < 1) {
    results.messages.push(
      "Company requires at least one Shareholder. Please appoint a Shareholder."
    );
    results.isValid = false;
  } else {
  }
};

// Helper function to validate share allocation
const validateShareAllocation = (company, shareholders) => {
  const results = {
    isValid: true,
    messages: [],
  };

  // Get total shares from Share Capital Tab
  // const nominalShares = company.nominalShares || [];
  // const totalIssuedShares = nominalShares.reduce((total, share) => {
  //   return total + (share.sharesNumber || 0);
  // }, 0);

  return results;
};
